import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Pencil } from 'lucide-react';
import { DebriefFactSource } from '@momentum/types';
import { cn } from '@/shared/utils/utils';
import { parseLocalYmd } from '@/shared/utils/date-utils';
import { useDebriefFactsState } from '../../shared/context/DebriefFactsStateContext';
import type { EditableFact } from '../../shared/types';
import { toViewableFactUrl } from '../../shared/utils/factLink';
import { FactDialog } from './FactDialog';
import { IntegrationIcon } from './IntegrationIcon';

interface FactRowProps {
  fact: EditableFact;
}

export function FactRow({ fact }: FactRowProps) {
  const { t, i18n } = useTranslation('debriefV2');
  const { editFact } = useDebriefFactsState();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const aiSource =
    fact.source !== DebriefFactSource.Manual ? fact.source : null;
  const isManual = aiSource === null;

  return (
    <>
      <div className="group flex items-center gap-3 py-1.5 relative rounded-lg -ms-2 ps-2 pe-2 border border-transparent transition-all hover:bg-white hover:shadow-[0_1px_4px_rgba(0,0,0,0.05)] hover:border-slate-200">
        <div className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" />
        <div className="flex-1 min-w-0 pe-16">
          <p className="text-sm text-slate-800 leading-relaxed inline">
            {fact.description}
          </p>
          {aiSource && <SourceChip source={aiSource} title={fact.title} />}
          {isManual && fact.date && (
            <DateChip date={fact.date} locale={i18n.language} />
          )}
        </div>
        <div className="absolute end-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            title={t('gather.factRow.edit')}
            aria-label={t('gather.factRow.edit')}
            className="bg-white/80 backdrop-blur-sm p-1.5 rounded-md border border-slate-200 shadow-sm text-slate-400 hover:text-primary"
          >
            <Pencil className="h-4 w-4" aria-hidden />
          </button>
          {!isManual && (
            <a
              href={toViewableFactUrl(fact.link)}
              target="_blank"
              rel="noopener noreferrer"
              title={t('gather.factRow.viewSource')}
              aria-label={t('gather.factRow.viewSource')}
              className="bg-white/80 backdrop-blur-sm p-1.5 rounded-md border border-slate-200 shadow-sm text-slate-400 hover:text-primary"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
          )}
        </div>
      </div>

      <FactDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        mode="edit"
        stage={fact.stage}
        showDateField={isManual}
        initialTitle={fact.title}
        initialDescription={fact.description}
        initialDate={fact.date}
        onSave={(draft) => editFact(fact.id, draft)}
      />
    </>
  );
}

type AiFactSource = Exclude<DebriefFactSource, DebriefFactSource.Manual>;

interface SourceChipProps {
  source: AiFactSource;
  title: string;
}

const SOURCE_CHIP_CLASS: Record<AiFactSource, string> = {
  [DebriefFactSource.Jira]: 'bg-blue-50 border-blue-100 text-blue-700',
  [DebriefFactSource.Confluence]: 'bg-sky-50 border-sky-100 text-sky-700',
  [DebriefFactSource.Sharepoint]:
    'bg-white border-slate-200 text-slate-600 shadow-sm',
};

function SourceChip({ source, title }: SourceChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 ms-2 px-2 py-0.5 rounded-md border text-[11px] font-semibold align-middle',
        SOURCE_CHIP_CLASS[source]
      )}
    >
      <IntegrationIcon source={source} className="h-3 w-3" />
      {title}
    </span>
  );
}

interface DateChipProps {
  date: string;
  locale: string;
}

function DateChip({ date, locale }: DateChipProps) {
  const parsed = parseLocalYmd(date);
  if (!parsed) return null;
  const formatted = parsed.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <span className="inline-flex items-center gap-1 ms-2 px-2 py-0.5 rounded-md border bg-slate-50 border-slate-200 text-slate-600 text-[11px] font-medium align-middle">
      {formatted}
    </span>
  );
}
